package com.patronat.top.features.venues.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import com.patronat.top.dto.UNASSIGNED_VENUE_OWNER_ID
import com.patronat.top.dto.VenueDraftState
import com.patronat.top.dto.UserRole
import com.patronat.top.dto.venues.VenueCategory
import com.patronat.top.dto.venues.VenueStatus
import com.patronat.top.ui.models.VenueModeratorOption
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.venue_button_cancel
import top.features.venues.generated.resources.venue_button_create
import top.features.venues.generated.resources.venue_button_reject
import top.features.venues.generated.resources.venue_button_save_as_draft
import top.features.venues.generated.resources.venue_day_fri
import top.features.venues.generated.resources.venue_day_mon
import top.features.venues.generated.resources.venue_day_sat
import top.features.venues.generated.resources.venue_day_sun
import top.features.venues.generated.resources.venue_day_thu
import top.features.venues.generated.resources.venue_day_tue
import top.features.venues.generated.resources.venue_day_wed
import top.features.venues.generated.resources.venue_button_submit
import top.features.venues.generated.resources.venue_moderator_selector_placeholder
import top.features.venues.generated.resources.venue_moderator_selector_title_prefix
import top.features.venues.generated.resources.venue_role_moderator
import top.features.venues.generated.resources.venue_role_super_admin
import top.features.venues.generated.resources.venue_service_submit

@Composable
fun VenueFormRoute(
    mode: VenueFormMode,
    currentUserRole: UserRole? = null,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    pickServicesPdfFile: suspend () -> VenuePickedFile? = { null },
    pickGalleryImageFile: suspend () -> VenuePickedFile? = { null }
) {
    val viewModel = koinInject<VenueFormViewModel>()
    val state by viewModel.viewState.collectAsState()

    val dayMon = stringResource(Res.string.venue_day_mon)
    val dayTue = stringResource(Res.string.venue_day_tue)
    val dayWed = stringResource(Res.string.venue_day_wed)
    val dayThu = stringResource(Res.string.venue_day_thu)
    val dayFri = stringResource(Res.string.venue_day_fri)
    val daySat = stringResource(Res.string.venue_day_sat)
    val daySun = stringResource(Res.string.venue_day_sun)
    val approveLabel = stringResource(Res.string.venue_button_submit)
    val createLabel = stringResource(Res.string.venue_button_create)
    val cancelLabel = stringResource(Res.string.venue_button_cancel)
    val saveAsDraftLabel = stringResource(Res.string.venue_button_save_as_draft)
    val rejectLabel = stringResource(Res.string.venue_button_reject)
    val moderationLabel = stringResource(Res.string.venue_service_submit)
    val moderatorTitlePrefix = stringResource(Res.string.venue_moderator_selector_title_prefix)
    val moderatorPlaceholder = stringResource(Res.string.venue_moderator_selector_placeholder)
    val moderatorRoleLabel = stringResource(Res.string.venue_role_moderator)
    val superAdminRoleLabel = stringResource(Res.string.venue_role_super_admin)
    val localizedCategory = state.mainInfo.category?.let { option ->
        val category = VenueCategory.entries.firstOrNull { it.name == option.id }
        if (category != null) {
            option.copy(label = stringResource(category.labelRes()))
        } else {
            option
        }
    }
    val localizedMainInfo = state.mainInfo.copy(category = localizedCategory)
    val moderatorOptions = state.moderatorAssignees.map { assignee ->
        VenueModeratorOption(
            id = assignee.id,
            fullName = listOf(assignee.firstName, assignee.lastName)
                .filter { part -> part.isNotBlank() }
                .joinToString(" ")
                .ifBlank { moderatorPlaceholder },
            roleLabel = assignee.role.toModeratorRoleLabel(
                moderatorLabel = moderatorRoleLabel,
                superAdminLabel = superAdminRoleLabel
            ),
            avatarUrl = assignee.avatarUrl
        )
    }
    val selectedModerator = moderatorOptions.firstOrNull { option ->
        option.id == state.venueModeratorId
    }
    val selectedModeratorName = selectedModerator?.fullName ?: moderatorPlaceholder
    val showModeratorSelector = mode is VenueFormMode.Edit && currentUserRole.canManageVenueModerator()
    val showOwnerSection = mode is VenueFormMode.Edit &&
        currentUserRole.canViewOwnerSection() &&
        !state.ownerId.isNullOrBlank() &&
        state.ownerId != UNASSIGNED_VENUE_OWNER_ID
    val isCreateMode = mode is VenueFormMode.Create || mode is VenueFormMode.CreateFromAi
    val isOwner = currentUserRole == UserRole.BUSINESS_OWNER
    val isModerator = currentUserRole.isModeratorOrSuperAdmin()
    val isModeratorEditMode = mode is VenueFormMode.Edit && currentUserRole.isModeratorOrSuperAdmin()
    val hasSubmittedDraft = state.draftState == VenueDraftState.APPLICATION
    val isModeratorDraftFlow = isModerator && (
        isCreateMode || state.venueStatus == VenueStatus.DRAFT
    )
    val showSaveDraftAction = (isOwner && (isCreateMode || mode is VenueFormMode.Edit)) ||
        isModeratorDraftFlow
    val showRejectAction = isModeratorEditMode && hasSubmittedDraft
    val submitLabel = when {
        showRejectAction -> approveLabel
        isModeratorDraftFlow -> createLabel
        isOwner -> moderationLabel
        else -> approveLabel
    }
    val cancelActionLabel = when {
        showRejectAction -> rejectLabel
        showSaveDraftAction -> saveAsDraftLabel
        else -> cancelLabel
    }
    val aiPaths = state.aiFlaggedFieldPaths
    val mainNameError = aiPaths.hasField("main.name")
    val mainCategoryError = aiPaths.hasField("main.category")
    val mainPhoneError = aiPaths.hasField("main.phone")
    val mainWebsiteError = aiPaths.hasField("main.website")
    val mainDescriptionError = aiPaths.hasField("main.description")
    val locationAddressError = aiPaths.hasField("location.address")
    val scheduleError = aiPaths.hasField("schedule")
    val galleryError = aiPaths.hasField("gallery")

    LaunchedEffect(mode, currentUserRole, dayMon, dayTue, dayWed, dayThu, dayFri, daySat, daySun) {
        viewModel.onEvent(
            VenueFormEvent.Initialize(
                mode = mode,
                scheduleDayLabels = VenueScheduleDayLabels(
                    monday = dayMon,
                    tuesday = dayTue,
                    wednesday = dayWed,
                    thursday = dayThu,
                    friday = dayFri,
                    saturday = daySat,
                    sunday = daySun
                ),
                currentUserRole = currentUserRole
            )
        )
    }

    LaunchedEffect(viewModel) {
        viewModel.effect.collect { effect ->
            when (effect) {
                VenueFormEffect.NavigateBack -> onBack()
                VenueFormEffect.OpenGalleryImagePicker -> {
                    val file = pickGalleryImageFile()
                    if (file != null) {
                        viewModel.onEvent(VenueFormEvent.OnGalleryFilePicked(file))
                    }
                }

                VenueFormEffect.OpenServicesPdfPicker -> {
                    val file = pickServicesPdfFile()
                    if (file != null) {
                        viewModel.onEvent(VenueFormEvent.OnServicesFilePicked(file))
                    }
                }
            }
        }
    }

    VenueFormScreen(
        ownerInfo = state.ownerInfo,
        onOwnerInfoChange = { viewModel.onEvent(VenueFormEvent.OnOwnerInfoChanged(it)) },
        mainInfo = localizedMainInfo,
        onMainInfoChange = { viewModel.onEvent(VenueFormEvent.OnMainInfoChanged(it)) },
        scheduleDays = state.scheduleDays,
        onScheduleChange = { viewModel.onEvent(VenueFormEvent.OnScheduleChanged(it)) },
        locationData = state.locationData,
        onLocationDataChange = { viewModel.onEvent(VenueFormEvent.OnLocationDataChanged(it)) },
        venueMarkers = state.venueMarkers,
        mainInfoNameError = mainNameError,
        mainInfoCategoryError = mainCategoryError,
        mainInfoPhoneError = mainPhoneError,
        mainInfoWebsiteError = mainWebsiteError,
        mainInfoDescriptionError = mainDescriptionError,
        locationAddressError = locationAddressError,
        scheduleError = scheduleError,
        galleryError = galleryError,
        galleryErrorPhotoIds = state.aiGalleryErrorIds,
        serviceErrorIds = state.aiServiceErrorIds,
        galleryPhotos = state.galleryPhotos,
        onGalleryPhotosChange = { viewModel.onEvent(VenueFormEvent.OnGalleryPhotosChanged(it)) },
        onGalleryAddClick = { viewModel.onEvent(VenueFormEvent.OnGalleryAddClicked) },
        services = state.services,
        onServicesChange = { viewModel.onEvent(VenueFormEvent.OnServicesChanged(it)) },
        selectedTab = state.selectedTab,
        onSelectedTabChange = { viewModel.onEvent(VenueFormEvent.OnSelectedTabChanged(it)) },
        showModeratorSelector = showModeratorSelector,
        moderatorTitlePrefix = moderatorTitlePrefix,
        moderatorSelectedName = selectedModeratorName,
        moderatorOptions = moderatorOptions,
        moderatorSelectorExpanded = state.isModeratorMenuExpanded,
        moderatorSelectorEnabled = !state.isModeratorReassigning,
        moderatorSelectorLoading = state.isModeratorAssigneesLoading || state.isModeratorReassigning,
        onModeratorMenuExpandedChange = { expanded ->
            viewModel.onEvent(
                if (expanded) {
                    VenueFormEvent.OnModeratorSelectorClicked
                } else {
                    VenueFormEvent.OnDismissModeratorMenu
                }
            )
        },
        onModeratorSelected = { moderatorId ->
            viewModel.onEvent(VenueFormEvent.OnModeratorSelected(moderatorId))
        },
        showOwnerSection = showOwnerSection,
        attachedServicesFileName = state.attachedServicesFileName,
        onUploadServicesFileClick = { viewModel.onEvent(VenueFormEvent.OnUploadServicesFileClicked) },
        onServicesFileRemoveClick = { viewModel.onEvent(VenueFormEvent.OnServicesFileRemoveClicked) },
        onAddServiceClick = { viewModel.onEvent(VenueFormEvent.OnAddServiceClicked) },
        onSaveServicesDraftClick = {
            when {
                showRejectAction -> viewModel.onEvent(VenueFormEvent.OnVenueRejectClicked)
                showSaveDraftAction -> viewModel.onEvent(VenueFormEvent.OnSaveServicesDraftClicked)
                else -> viewModel.onEvent(VenueFormEvent.OnVenueCancelClicked)
            }
        },
        onSubmitServicesClick = { viewModel.onEvent(VenueFormEvent.OnSubmitServicesClicked) },
        onVenueCancelClick = {
            when {
                showRejectAction -> viewModel.onEvent(VenueFormEvent.OnVenueRejectClicked)
                showSaveDraftAction -> viewModel.onEvent(VenueFormEvent.OnSaveServicesDraftClicked)
                else -> viewModel.onEvent(VenueFormEvent.OnVenueCancelClicked)
            }
        },
        onVenueSubmitClick = { viewModel.onEvent(VenueFormEvent.OnVenueSubmitClicked) },
        venueSubmitLoading = state.isSubmitting || state.isGalleryUploading || state.isServicesFileUploading,
        venueCancelButtonText = cancelActionLabel,
        venueSubmitButtonText = submitLabel,
        serviceSaveDraftButtonText = cancelActionLabel,
        serviceSubmitButtonText = submitLabel,
        ownerReadOnly = state.ownerReadOnly,
        modifier = modifier
    )
}

private fun UserRole?.canViewOwnerSection(): Boolean {
    return this == UserRole.MODERATOR || this == UserRole.SUPER_ADMIN
}

private fun UserRole?.canManageVenueModerator(): Boolean {
    return this == UserRole.MODERATOR || this == UserRole.SUPER_ADMIN
}

private fun UserRole?.isModeratorOrSuperAdmin(): Boolean {
    return this == UserRole.MODERATOR || this == UserRole.SUPER_ADMIN
}

private fun Set<String>.hasField(path: String): Boolean {
    if (contains(path)) return true
    return any { value ->
        value.startsWith("$path.") || value.startsWith("$path[")
    }
}

private fun UserRole.toModeratorRoleLabel(
    moderatorLabel: String,
    superAdminLabel: String
): String {
    return when (this) {
        UserRole.MODERATOR -> moderatorLabel
        UserRole.SUPER_ADMIN -> superAdminLabel
        else -> name
    }
}
